import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import useRecaptcha from 'hooks/useRecaptcha';
import useDraggableModal from 'hooks/useDraggableModal';
import { showError, showSuccess } from 'utils/utils';
import { validate } from 'utils/validation';

import { useSearchParams } from 'react-router-dom';
import { ContactUsModel } from 'ts/package/classes/ContactUsModel';
import { sendContactUs } from 'services/package';

interface SendRequestModalProps {
  onClose: () => void;
}

const SendRequestModal: FC<SendRequestModalProps> = ({ onClose }) => {
  const [t] = useTranslation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [urlParams] = useSearchParams();
  const [model, setModel] = useState(new ContactUsModel(urlParams.get('contactUsText')));
  const { handleRecapcha } = useRecaptcha();
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);

  const validation = {
    firstName: {
      required: true,
    },
    phone: {
      required: true,
      numbers: true,
    },
    email: {
      email: true,
    },
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setIsSubmitted(true);

    const isValid = validate(validation, model);
    if (isValid && model.message) {
      const token = await handleRecapcha();

      if (token) {
        const res = await sendContactUs({ ...model, recaptchaToken: token, lastName: '1' });
        onClose();
        if (res?.hasErrors) {
          showError(t('something_went_wrong'));
        } else {
          setModel(new ContactUsModel());
          setIsSubmitted(false);
          showSuccess(t('login.contact_us_success'));
        }
      }
    }

    setIsLoading(false);
  };

  return (
    <div
      className="modal-overlay modal-overlay-top modal-maximizable"
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={'modal modal-sm active'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">שלח בקשה</h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content text-sm rtl">
          <div className="flex-row">
            <div className="title col-12">פרטי המזמין</div>
            <FormGroup
              className="col-12 col-md-6 mb-20"
              label={t('login.first_name')}
              value={model.firstName}
              onChange={(firstName) => setModel({ ...model, firstName })}
              movablePlaceholder={t('passenger.fn')}
              isSubmitted={isSubmitted}
              validation={validation.firstName}
            />
            <FormGroup
              className="col-12 col-md-6 mb-20"
              label={t('login.last_name')}
              value={model.lastName || ''}
              onChange={(lastName) => setModel({ ...model, lastName })}
              movablePlaceholder={t('passenger.ln')}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-12 col-md-6 mb-20"
              label={t('passenger.email')}
              value={model.email}
              onChange={(email) => setModel({ ...model, email })}
              movablePlaceholder={t('passenger.email')}
              isSubmitted={isSubmitted}
              validation={validation.email}
            />
            <FormGroup
              className="col-12 col-md-6 mb-20"
              label={t('passenger.phone')}
              value={model.phone}
              onChange={(phone) => setModel({ ...model, phone })}
              movablePlaceholder={t('passenger.phone')}
              isSubmitted={isSubmitted}
              validation={validation.phone}
            />
            <div className="col-12">
              <div className={'form-group' + (isSubmitted && !model.message ? ' is-invalid' : '')}>
                <textarea
                  className={`form-control`}
                  value={model.message}
                  onChange={(e) => setModel({ ...model, message: e.target.value })}
                  style={{ height: '150px', resize: 'none' }}
                />
                {isSubmitted && !model.message && (
                  <div className="form-invalid-text">{t('validation.required')}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button title={t('close')} className="btn btn-default" onClick={() => onClose()} />
          <Button
            title={t('mo.send')}
            className="btn btn-primary"
            disabled={isLoading}
            onClick={onSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default SendRequestModal;
